import {
  Alert,
  Anchor,
  Badge,
  Button,
  Code,
  Group,
  Select,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { listPortalReports, type PortalReportSummary, type ReportReviewState } from "@/api/portal";
import { RequirePermission } from "@/auth/guards";
import { useIAMAuth } from "@/auth/iam";
import { config } from "@/config";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t, type MessageKey } from "@/i18n";
import {
  formatBirthDate,
  formatWhen,
  orderReports,
  reportErrorKind,
  sexLabel,
} from "@/pages/portal/reportReview";

/**
 * Lab portal: this lab's Smart Reports, with where each stands in review (Gate B M1 S7).
 *
 * Served by onboarding-api's `/api/v1/portal/reports` (S6), which exists only with
 * SR_PORTAL_REPORTS_ENABLED on, and which takes the lab from the signed-in doctor's own
 * token. The route is guarded on the clinical read permission that BFF enforces.
 *
 * A list row carries no report body. It does carry whose report it is (docs#15 note 16595
 * item 1): the patient's name, date of birth and sex and the lab's order number, so a doctor
 * can find a patient and open the right report. Opening the list is not audited (see the
 * caveat on the MR), opening a report is, server-side, as `report.viewed`.
 *
 * Critical first, then newest (Baher, 2026-10-04): a row whose report has any Critical
 * result comes first, then every other row, newest first inside each. Recommended does not
 * rank above None. The order keys on the row's `highest_severity`, which onboarding-api adds
 * to the list rows. A row without it (an older BFF) ranks with the non-Critical rows, so
 * before that field exists the list is simply newest first. The server pages, so this
 * orders within the page it returns. Across pages the order is the server's, which sorts the
 * same way (the orchestrator orders before paging).
 *
 * Nothing here approves, releases or withdraws a report. Those are M2 (S11).
 *
 * The error state is a fixed sentence per kind. The server's own text is never shown.
 */

const PAGE_SIZE = 50;

const FILTERS: Record<string, ReportReviewState[]> = {
  all: [],
  pending_review: ["pending_review"],
  approved: ["approved"],
  released: ["released"],
  withdrawn: ["withdrawn"],
};

export const REVIEW_STATE_LABEL: Record<ReportReviewState, MessageKey> = {
  pending_review: "portal.review_state_pending_review",
  approved: "portal.review_state_approved",
  released: "portal.review_state_released",
  withdrawn: "portal.review_state_withdrawn",
};

export const REVIEW_STATE_COLOR: Record<ReportReviewState, string> = {
  pending_review: "orange",
  approved: "blue",
  released: "green",
  withdrawn: "gray",
};

export function ReviewStateBadge({
  state,
}: {
  state: ReportReviewState | null | undefined;
}): JSX.Element {
  if (state === null || state === undefined || !(state in REVIEW_STATE_LABEL)) {
    return <Text c="dimmed">{t("portal.review_state_none")}</Text>;
  }
  return (
    <Badge color={REVIEW_STATE_COLOR[state]} variant="light">
      {t(REVIEW_STATE_LABEL[state])}
    </Badge>
  );
}

const SEVERITY_COLOR: Record<string, string> = { critical: "red", recommended: "orange" };

/**
 * A null severity is a dash, never "None": null means nothing was judged (a report holding
 * only passthrough rows, or an orchestrator that predates the field), while the string
 * "None" is a judgement that the result needs nothing. A lab doctor must see the difference.
 */
export function SeverityBadge({ severity }: { severity: string | null }): JSX.Element {
  if (severity === null) return <Text c="dimmed">—</Text>;
  const key = severity.toLowerCase();
  const color = SEVERITY_COLOR[key];
  if (color === undefined) {
    return <Text c="dimmed">{t("portal.report_review_severity_none")}</Text>;
  }
  return (
    <Badge color={color} variant="filled">
      {severity}
    </Badge>
  );
}

export function PortalReportsPage(): JSX.Element {
  const { organizationId } = useIAMAuth();
  const [filter, setFilter] = useState("all");
  const [offset, setOffset] = useState(0);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["portal-reports", filter, offset],
    queryFn: () =>
      listPortalReports({ reviewStates: FILTERS[filter] ?? [], limit: PAGE_SIZE, offset }),
    retry: false,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });

  const total = data?.total ?? 0;
  const shownTo = Math.min(offset + PAGE_SIZE, total);

  return (
    <AppLayout breadcrumbs={[{ label: t("portal.reports") }]}>
      <Group justify="space-between" align="baseline">
        <Title order={1}>{t("portal.reports")}</Title>
        <Text c="dimmed" size="sm">
          {t("portal.organization")}:{" "}
          {data?.organization_name ? (
            <span data-testid="reports-lab-name">{data.organization_name}</span>
          ) : (
            <Code>{organizationId}</Code>
          )}
        </Text>
      </Group>
      <Text c="dimmed" mt="xs">
        {t("portal.reports_subtitle")}
      </Text>
      {/* This list is the landing page for a doctor (docs#15 note 16595 item 2), so their
          own signature, which the home page used to link, is reachable from here. */}
      {config.brandingEnabled ? (
        <RequirePermission perm="branding:signature">
          <Button component={Link} to="/portal/signature" variant="subtle" size="xs" mt="xs">
            {t("home.open_signature")}
          </Button>
        </RequirePermission>
      ) : null}

      <Group mt="md" mb="md" align="flex-end">
        <Select
          label={t("portal.reports_filter")}
          data={[
            { value: "all", label: t("portal.reports_filter_all") },
            { value: "pending_review", label: t("portal.review_state_pending_review") },
            { value: "approved", label: t("portal.review_state_approved") },
            { value: "released", label: t("portal.review_state_released") },
            { value: "withdrawn", label: t("portal.review_state_withdrawn") },
          ]}
          value={filter}
          allowDeselect={false}
          onChange={(v) => {
            if (v !== null) {
              setFilter(v);
              setOffset(0);
            }
          }}
        />
      </Group>

      {isPending ? (
        <TableSkeleton label={t("portal.reports_loading")} />
      ) : isError ? (
        <Alert color="red" title={t("portal.reports")}>
          {reportErrorKind(error) === "forbidden"
            ? t("portal.reports_forbidden")
            : t("portal.reports_unavailable")}
        </Alert>
      ) : data.reports.length === 0 ? (
        <Text c="dimmed">
          {filter === "all" ? t("portal.reports_empty") : t("portal.reports_empty_filtered")}
        </Text>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="xs">
            {t("portal.reports_showing")} {offset + 1}–{shownTo} {t("portal.reports_of")} {total}{" "}
            {t("portal.reports_count_suffix")}
          </Text>
          <ReportTable
            reports={orderReports(data.reports)}
            timeZone={data.organization_timezone ?? null}
          />
          <Group mt="md">
            <Button
              variant="default"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              {t("portal.reports_prev")}
            </Button>
            <Button
              variant="default"
              disabled={shownTo >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              {t("portal.reports_next")}
            </Button>
          </Group>
        </>
      )}
    </AppLayout>
  );
}

function ReportTable({
  reports,
  timeZone,
}: {
  reports: PortalReportSummary[];
  timeZone: string | null;
}): JSX.Element {
  const missing = t("portal.identity_not_available");
  return (
    <Table striped highlightOnHover withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("portal.reports_col_patient")}</Table.Th>
          <Table.Th>{t("portal.reports_col_birth_date")}</Table.Th>
          <Table.Th>{t("portal.reports_col_sex")}</Table.Th>
          <Table.Th>{t("portal.reports_col_order")}</Table.Th>
          <Table.Th>{t("portal.reports_col_report")}</Table.Th>
          <Table.Th>{t("portal.reports_col_generated")}</Table.Th>
          <Table.Th>{t("portal.reports_col_severity")}</Table.Th>
          <Table.Th>{t("portal.reports_col_review")}</Table.Th>
          <Table.Th>{t("portal.reports_col_status")}</Table.Th>
          <Table.Th>{t("portal.reports_col_supersedes")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {reports.map((report) => {
          const patient = report.patient ?? null;
          const to = `/portal/reports/${encodeURIComponent(report.report_id)}`;
          return (
            <Table.Tr key={report.report_id} data-testid="reports-row">
              <Table.Td>
                <Anchor component={Link} to={to} data-testid="reports-patient">
                  {patient?.display_name ?? missing}
                </Anchor>
              </Table.Td>
              <Table.Td data-testid="reports-birth-date">
                {formatBirthDate(patient?.birth_date) ?? "—"}
              </Table.Td>
              <Table.Td data-testid="reports-sex">{sexLabel(patient?.sex) ?? "—"}</Table.Td>
              <Table.Td data-testid="reports-order">{report.lab_order_number ?? "—"}</Table.Td>
              <Table.Td>
                <ReportIds report={report} to={to} />
              </Table.Td>
              <Table.Td data-testid="reports-generated">
                {formatWhen(report.generated_at, timeZone)}
              </Table.Td>
              <Table.Td>
                {report.highest_severity == null ? (
                  "—"
                ) : (
                  <SeverityBadge severity={report.highest_severity} />
                )}
              </Table.Td>
              <Table.Td>
                <ReviewStateBadge state={report.review_state} />
              </Table.Td>
              <Table.Td>{report.report_status}</Table.Td>
              <Table.Td>
                {report.supersedes_report_id ? <Code>{report.supersedes_report_id}</Code> : "—"}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

/**
 * The report's ids, the lab's first (docs#239 item 26): the `rpt_` id the lab's own system
 * was told, so a doctor can match the row to the lab's message, with our store id under it,
 * dimmed. With lab report events off the lab was told the store id, so it is shown as before.
 * The link opens by the store id either way.
 */
function ReportIds({ report, to }: { report: PortalReportSummary; to: string }): JSX.Element {
  const labId = report.lab_report_id ?? null;
  if (labId === null) {
    return (
      <Anchor component={Link} to={to} size="xs" c="dimmed">
        <Code data-testid="reports-store-id">{report.report_id}</Code>
      </Anchor>
    );
  }
  return (
    <>
      <Anchor component={Link} to={to} size="xs">
        <Code data-testid="reports-lab-report-id">{labId}</Code>
      </Anchor>
      <Text size="xs" c="dimmed" data-testid="reports-store-id">
        {t("portal.identity_report_id")}: {report.report_id}
      </Text>
    </>
  );
}
